/**
 * Queue Client — thin wrapper over worker HTTP API.
 *
 * Browser calls -> nginx /api/queue/* -> worker:3001 (Hono) -> BullMQ enqueue.
 *
 * Each call attaches the current Supabase session JWT in Authorization.
 * nginx (proxy_set_header Authorization $http_authorization) forwards it to
 * worker auth middleware. 401 response -> session expired, caller redirects.
 */
import { supabase } from "@/integrations/supabase/client";

const API_BASE = "/api/queue";

async function bearerHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new SessionExpiredError("Brak aktywnej sesji Supabase");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

export class SessionExpiredError extends Error {
  constructor(msg = "Sesja wygasla — zaloguj sie ponownie") {
    super(msg);
    this.name = "SessionExpiredError";
  }
}

export class WorkerApiError extends Error {
  constructor(public status: number, public details: unknown, msg: string) {
    super(msg);
    this.name = "WorkerApiError";
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const headers = await bearerHeader();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    throw new SessionExpiredError();
  }

  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!res.ok) {
    throw new WorkerApiError(
      res.status,
      parsed,
      `Worker API ${path} failed (${res.status})`,
    );
  }

  return parsed as T;
}

// =============================================================================
// Public API
// =============================================================================

export interface EnqueueResponse {
  jobId: string;
  enqueued: number;
  stages?: GenerationStages;
  force?: boolean;
}

export type GenerationStages = "structure" | "content" | "full";

export interface EnqueueGenerationOptions {
  /** Which pipeline phase to run. Default 'full' (Etap 1 + Etap 2). */
  stages?: GenerationStages;
  /**
   * Bypass 'already_done' guard for ready/published nodes.
   * Worker will hard-reset content (delete html + KG) before regenerating.
   * Use only after explicit user confirm with cost/word-count display.
   */
  force?: boolean;
}

/**
 * Enqueue a generation job for N nodes of a map.
 * Worker picks it up from Redis and processes per concurrency+limiter settings.
 */
export async function enqueueGenerationJob(
  mapId: string,
  nodeIds: string[],
  options: EnqueueGenerationOptions = {},
): Promise<EnqueueResponse> {
  const body: Record<string, unknown> = { mapId, nodeIds };
  if (options.stages) body.stages = options.stages;
  if (options.force) body.force = true;
  return postJson<EnqueueResponse>("/generation", body);
}

/**
 * Enqueue a publish job for N nodes targeting a specific website.
 */
export async function enqueuePublishJob(
  mapId: string,
  websiteId: string,
  nodeIds: string[],
): Promise<EnqueueResponse> {
  return postJson<EnqueueResponse>("/publish", { mapId, websiteId, nodeIds });
}

// =============================================================================
// Map Step enqueue (seeds/nodes przez worker container)
// =============================================================================

export type MapStep =
  | "seeds"
  | "nodes"
  | "nodes_batch"
  | "entities"
  | "eav"
  | "linking"
  | "audit"
  | "writing_profile";

export interface EnqueueMapStepResponse {
  queueRowId: string;
  jobId: string;
  step: `map_${MapStep}`;
}

export interface EnqueueMapStepOptions {
  /** Wymagane dla step='nodes' — per seed */
  seedId?: string;
  /** Wymagane dla step='nodes_batch' — sekwencyjny batch z growing dedup */
  seedIds?: string[];
  /** Parallel grupy w nodes_batch (default 1 = strict sequential) */
  parallel?: number;
  /** Dodatkowy config (opcjonalny) */
  config?: Record<string, unknown>;
}

/**
 * Enqueue seed/node/batch generation do workera.
 *
 * @param step 'seeds'       — per mapa
 *             'nodes'       — per seed (wymaga options.seedId)
 *             'nodes_batch' — sekwencyjny batch (wymaga options.seedIds[])
 *
 * @throws WorkerApiError 409 gdy juz trwa taki sam job
 */
export async function enqueueMapStep(
  step: MapStep,
  mapId: string,
  options: EnqueueMapStepOptions = {},
): Promise<EnqueueMapStepResponse> {
  if (step === "nodes" && !options.seedId) {
    throw new Error("enqueueMapStep: step='nodes' requires seedId");
  }
  if (step === "nodes_batch" && (!options.seedIds || options.seedIds.length === 0)) {
    throw new Error("enqueueMapStep: step='nodes_batch' requires non-empty seedIds[]");
  }
  const body: Record<string, unknown> = { step, mapId };
  if (options.seedId) body.seedId = options.seedId;
  if (options.seedIds) body.seedIds = options.seedIds;
  if (options.parallel) body.parallel = options.parallel;
  if (options.config) body.config = options.config;
  return postJson<EnqueueMapStepResponse>("/map-step", body);
}

// =============================================================================
// Admin: clear queue (KILL SWITCH)
// =============================================================================

export type ClearQueueScope = "all" | "generation" | "publish" | "map_steps_only";

export interface ClearQueueResponse {
  ok: boolean;
  scope: ClearQueueScope;
  bullmq: { generation: number; publish: number };
  db: { generation: number; publish: number };
  note: string;
}

/**
 * Wyczyszczenie kolejek — drain BullMQ + UPDATE DB rows na cancelled.
 * Worker dokonczy aktualnie procesowany job ale NIE wezmie nowych.
 *
 * @param scope "all" (default) — wszystkie 2 queue
 *              "generation"    — tylko generation_queue (CE + map_*)
 *              "publish"       — tylko publish_queue
 *              "map_steps_only" — tylko map_seeds/nodes/entities/eav/linking/audit/wp
 *                                 (NIE rusza CE pełnych artykulow)
 */
export async function clearQueue(scope: ClearQueueScope = "all"): Promise<ClearQueueResponse> {
  return postJson<ClearQueueResponse>("/admin/clear-queue", { scope });
}

// =============================================================================
// Senuto sync — per-website SEO signals przez workera (Node fetch, zero CORS)
// =============================================================================

export interface EnqueueSenutoSyncResponse {
  queueRowId: string;
  jobId: string;
  step: "senuto_sync";
}

/**
 * Enqueue Senuto sync. Worker robi direct fetch do api.senuto.com z Node
 * (Bearer z process.env.SENUTO_API_KEY) — zero browser CORS, zamkniesz kartę i leci.
 *
 * @param websiteId UUID website z tabeli websites
 * @param countryId "200" (Polska) default, opcjonalnie inne Senuto country_id
 *
 * @throws WorkerApiError 409 gdy juz trwa sync dla tego website
 */
export async function enqueueSenutoSync(
  websiteId: string,
  countryId?: string,
): Promise<EnqueueSenutoSyncResponse> {
  const body: Record<string, unknown> = { websiteId };
  if (countryId) body.countryId = countryId;
  return postJson<EnqueueSenutoSyncResponse>("/senuto-sync", body);
}

